import React, { useCallback, useContext, useEffect, useRef } from 'react';
import t from 'react-translate';
import { css } from '@emotion/react';
import {
  danger,
  genAIGradientType4,
  gray6,
  gray7,
  hexToRgbaString,
  white,
} from 'styles/global_defaults/colors';
import { doubleSpacing, halfSpacing, threeQuartersSpacing } from 'styles/global_defaults/scaffolding';
import NvIcon from 'shared/components/nv-icon';
import Spinner from 'shared/components/spinner';
import AiConversationContext, { AIRolePlayStatus, Role, VoiceCardState } from '../context';
import setupChromaKey from './chroma-key';

type Props = {
  role: Role;
  name: string;
  state: VoiceCardState;
  /** Learner card only: the camera stream shown as a live self-view. */
  mediaStream?: MediaStream;
  dataQa?: string;
};

const CARD_MAX_WIDTH = 480;
const PILL_HEIGHT = 30;
const PILL_BORDER_RADIUS = 15;
const PILL_OFFSET = halfSpacing;

const MicrophoneDisabledComponent = () => (
  <div
    className='disabled-microphone bg-white position-absolute d-flex align-items-center justify-content-center'
  >
    <NvIcon
      icon='audio-recording'
      size='small'
      className='d-flex align-items-center mx-auto'
    />
    <div className='mic-slash position-absolute' />
  </div>
);

/**
 * One participant tile of a video role play: the AI card renders the
 * LiveAvatar's LiveKit video/audio, the learner card renders a live (muted)
 * camera self-view. Keeps the voice cards' name pill and speaking-ring
 * conventions, in a 16:9 responsive frame.
 */
const VideoCard = ({ role, name, state, mediaStream, dataQa }: Props) => {
  const isSpeaking = state === VoiceCardState.SPEAKING;
  const isThinking = state === VoiceCardState.THINKING;

  const {
    microphoneEnabled,
    aiRolePlayStatus,
    attachAvatarVideo,
    avatarConnectionState,
    avatarBackgroundImageUrl,
  } = useContext(AiConversationContext);

  const selfViewRef = useRef<HTMLVideoElement>(null);
  const avatarVideoRef = useRef<HTMLVideoElement | null>(null);
  const chromaCanvasRef = useRef<HTMLCanvasElement>(null);

  // Learner self-view — the avatar card's element is wired through
  // attachAvatarVideo instead so LiveKit can attach tracks as they arrive.
  useEffect(() => {
    if (role !== Role.LEARNER || !selfViewRef.current) return;
    selfViewRef.current.srcObject = mediaStream ?? null;
  }, [role, mediaStream]);

  // LiveAvatar streams studio avatars on a green screen; when a custom
  // background is configured the raw video stays hidden (still playing — it
  // carries the avatar's audio) and the canvas shows the keyed frames over
  // the background image.
  const useChromaKey = role === Role.AI && !!avatarBackgroundImageUrl;

  const setAvatarVideoRef = useCallback((videoEl: HTMLVideoElement | null) => {
    avatarVideoRef.current = videoEl;
    attachAvatarVideo?.(videoEl);
  }, [attachAvatarVideo]);

  useEffect(() => {
    if (!useChromaKey || !avatarVideoRef.current || !chromaCanvasRef.current) return undefined;
    return setupChromaKey(avatarVideoRef.current, chromaCanvasRef.current);
  }, [useChromaKey]);

  const isAvatarConnecting = role === Role.AI
    && (avatarConnectionState === 'connecting' || avatarConnectionState === 'reconnecting' || !avatarConnectionState);

  const styles = css`
    width: 100%;
    max-width: ${CARD_MAX_WIDTH}px;
    aspect-ratio: 16 / 9;
    border-radius: 15px;
    position: relative;
    border: 3px solid transparent;
    box-shadow: 1px 2px 2px 0px #0000001A;
    background-color: ${hexToRgbaString(gray7, 0.8)};
    overflow: hidden;

    ${useChromaKey && css`
      background-image: url(${avatarBackgroundImageUrl});
      background-size: cover;
      background-position: center;
    `}

    ${isSpeaking && css`
      &::before {
        content: '';
        position: absolute;
        inset: -3px;
        border-radius: 15px;
        padding: 3px;
        background: ${genAIGradientType4};
        -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
        -webkit-mask-composite: xor;
        mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
        mask-composite: exclude;
        opacity: 0.8;
        z-index: 1;
        pointer-events: none;
      }
    `}

    .video-element {
      width: 100%;
      height: 100%;
      object-fit: cover;
      border-radius: 12px;
    }

    /* Keeps playing (it carries the avatar's audio) while the keyed canvas
       is what the learner sees. */
    .chroma-source {
      position: absolute;
      inset: 0;
      opacity: 0;
      pointer-events: none;
    }

    .connection-spinner {
      inset: 0;
      color: ${white};
    }

    .thinking-pill {
      top: ${PILL_OFFSET}px;
      right: ${PILL_OFFSET}px;
      height: ${PILL_HEIGHT}px;
      padding: 0 ${halfSpacing}px;
      background-color: ${hexToRgbaString(gray6, 0.7)};
      border-radius: ${PILL_BORDER_RADIUS}px;
      z-index: 2;
    }

    .name-pill {
      bottom: ${PILL_OFFSET}px;
      left: ${PILL_OFFSET}px;
      height: ${PILL_HEIGHT}px;
      max-width: calc(100% - ${PILL_OFFSET * 2}px);
      border-radius: ${PILL_BORDER_RADIUS}px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      background-color: ${hexToRgbaString(gray6, 0.7)};
      z-index: 2;
    }
    .disabled-microphone {
      top: ${PILL_OFFSET}px;
      right: ${PILL_OFFSET}px;
      width: ${doubleSpacing}px;
      height: ${doubleSpacing}px;
      border-radius: ${threeQuartersSpacing}px;
      z-index: 2;
      .mic-slash {
        width: ${doubleSpacing}px;
        height: 1px;
        background-color: ${danger};
        border-radius: 1px;
        transform: rotate(135deg);
        pointer-events: none;
      }
    }
  `;

  return (
    <div css={styles} className='position-relative d-flex align-items-center justify-content-center' data-qa={dataQa}>
      {role === Role.AI ? (
        <>
          <video
            className={`video-element ${useChromaKey ? 'chroma-source' : ''}`}
            ref={setAvatarVideoRef}
            autoPlay
            playsInline
            disablePictureInPicture
          />
          {useChromaKey && (
            <canvas className='video-element' ref={chromaCanvasRef} />
          )}
          {isAvatarConnecting && (
            <div className='connection-spinner position-absolute d-flex align-items-center justify-content-center'>
              <Spinner size={doubleSpacing} />
            </div>
          )}
        </>
      ) : (
        <video
          className='video-element'
          ref={selfViewRef}
          aria-label={t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.CONVERSATION_MODAL.YOUR_CAMERA()}
          muted
          autoPlay
          playsInline
          disablePictureInPicture
        />
      )}
      <div className='name-pill text-small d-flex position-absolute text-gray-1 align-items-center px-4 font-weight-bolder'>
        {name}
      </div>
      {isThinking && (
        <div className='thinking-pill position-absolute d-flex align-items-center justify-content-center'>
          <Spinner size={halfSpacing} />
        </div>
      )}
      {role === Role.LEARNER
      && !microphoneEnabled
      && aiRolePlayStatus !== AIRolePlayStatus.COMPLETED
      && (
        <MicrophoneDisabledComponent />
      )}
    </div>
  );
};

export default VideoCard;
